import { parsePhoneNumberFromString } from "libphonenumber-js";
import { countriesWithCurrencies } from "./currency";

// Not reusing getISOFromCountryName here — it defaults unmatched names to
// "us", which would make an unrelated/garbage country string silently parse
// a number as a (wrong but structurally valid) US number. A phone reader
// needs "no match" to mean "don't guess", not "assume US".
const isoFromCountryName = (countryName) => {
  if (!countryName) return null;
  const match = countriesWithCurrencies.find(
    (c) => c.name.toLowerCase().trim() === String(countryName).toLowerCase().trim()
  );
  return match ? match.code : null;
};

// Self-heals CRM phone numbers that were stored before country-code
// normalization existed (or arrived via the public API / Meta import without
// one). Tries the number as-is first (the common, already-correct case),
// then retries using the record's own `country` field as a region hint, and
// only falls back to the old raw "+rawPhone" behavior if neither parses —
// so a genuinely unparseable number never regresses below what the app did
// before this fix.
const resolvePhoneNumber = (rawPhone, countryName) => {
  if (!rawPhone) return { valid: false, e164: "", digits: "" };

  const digits = String(rawPhone).replace(/\D/g, "");
  if (!digits) return { valid: false, e164: "", digits: "" };

  const asIs = parsePhoneNumberFromString(`+${digits}`);
  if (asIs && asIs.isValid()) {
    return { valid: true, e164: asIs.number, digits: asIs.number.slice(1) };
  }

  const iso = isoFromCountryName(countryName);
  if (iso) {
    const withRegion = parsePhoneNumberFromString(digits, iso);
    if (withRegion && withRegion.isValid()) {
      return {
        valid: true,
        e164: withRegion.number,
        digits: withRegion.number.slice(1),
      };
    }
  }

  return { valid: false, e164: `+${digits}`, digits };
};

// For tel: links.
export const resolveDialableNumber = (rawPhone, countryName) =>
  resolvePhoneNumber(rawPhone, countryName).e164;

// For wa.me links (no leading "+").
export const resolveWhatsAppNumber = (rawPhone, countryName) =>
  resolvePhoneNumber(rawPhone, countryName).digits;

export default resolvePhoneNumber;
